import React, { FC, useEffect, useState } from 'react';
import { SentryProvider } from '@ldp/error-boundary';
import { IViewResolverLiteProps } from '@ldp/view-resolver-lite';
import { CommonContexts, ICommonContextsProps } from '@ldp/common-contexts';
import { ISDMContextsProps, SDMContexts } from '@ldp/sdm-contexts';
import { IPageMetaSeoProps, PageMetaSeo } from '@ldp/page-meta-seo';
import { SimpleI18nContextProvider, TSimpleI18nLanguage } from '@ldp/simple-i18n';
import { translations } from './translations';
import { CONFIG_ENVIRONMENT, useConfig } from './hooks';
import { ViewResolverSdmInnerSSR } from './components';
import PlpContextWrapper from './components/plp-context-wrapper/plp-context-wrapper.component';

/** Don't forget to add prop types */
export interface IViewResolverSdmComponentProps
  extends Pick<
    IViewResolverLiteProps,
    'pageId' | 'locale' | 'fetchOptions' | 'PageErrorFallback' | 'PageLoadingFallback' | 'queryConfig' | 'themeTokens'
  > {
  /** The object that sets the props for CommonContexts component */
  commonContexts: Omit<ICommonContextsProps, 'children'>;
  /** The object that sets the props for SDMContexts component */
  sdmContexts: Omit<ISDMContextsProps, 'children'>;
  /** The props to pass to the page meta seo */
  seo: Omit<IPageMetaSeoProps, 'children'>;
  /** The config environment */
  env?: CONFIG_ENVIRONMENT;
  /** The version of the BFF endpoint to use */
  apiVersion?: 'v1' | 'v2';
  /* Has existing Sentry instance */
  hadExistingSentry?: boolean;
  children?: React.ReactNode;
  /* if the view resolver is used for Drawer or Model */
  isDrawer?: boolean;
}

/** The view resolver component to be used to support SDUI power pages in SDM */
export const ViewResolverSdmComponent: FC<IViewResolverSdmComponentProps> = ({
  locale,
  pageId,
  seo,
  commonContexts,
  sdmContexts,
  env = CONFIG_ENVIRONMENT.DEVELOPMENT,
  PageErrorFallback = () => <div data-testid="default-page-error" />,
  PageLoadingFallback = () => <div data-testid="default-page-loading" />,
  hadExistingSentry = false,
  isDrawer,
  children,
  ...rest
}) => {
  const config = useConfig(env);
  const commonContextSettings = commonContexts?.settings;
  const baseFontValue = commonContextSettings?.baseFont;
  const designTokenPlatformVal = commonContextSettings?.designTokenPlatform;

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!config) {
    return <PageLoadingFallback />;
  }
  return (
    <SentryProvider useExistingSentry={hadExistingSentry}>
      <SimpleI18nContextProvider lang={locale as TSimpleI18nLanguage} translations={translations}>
        <CommonContexts {...commonContexts} settings={{ ...commonContexts.settings, isDrawer, env }}>
          {!isDrawer ? (
            <PlpContextWrapper>
              <ViewResolverSdmInnerSSR
                apiKey={config.apiKey}
                sduiBaseUrl={config.sduiBaseUrl}
                locale={locale}
                pageId={pageId}
                baseFont={baseFontValue}
                designTokenPlatform={designTokenPlatformVal}
                PageErrorFallback={PageErrorFallback}
                PageLoadingFallback={PageLoadingFallback}
                {...rest}
              >
                {/* <ThemeFontsLoader /> was moved to _app.tsx for SSR homepage to avoid font reset on focus */}
                {isClient && <PageMetaSeo {...seo} />}
                <SDMContexts {...sdmContexts}>{children}</SDMContexts>
              </ViewResolverSdmInnerSSR>
            </PlpContextWrapper>
          ) : (
            <ViewResolverSdmInnerSSR
              apiKey={config.apiKey}
              sduiBaseUrl={config.sduiBaseUrl}
              locale={locale}
              pageId={pageId}
              baseFont={baseFontValue}
              designTokenPlatform={designTokenPlatformVal}
              PageErrorFallback={PageErrorFallback}
              PageLoadingFallback={PageLoadingFallback}
              {...rest}
            >
              <SDMContexts {...sdmContexts}>{children}</SDMContexts>
            </ViewResolverSdmInnerSSR>
          )}
        </CommonContexts>
      </SimpleI18nContextProvider>
    </SentryProvider>
  );
};

export default ViewResolverSdmComponent;
